import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookMarked, Check, ArrowLeft, ArrowRight } from "lucide-react";
import { AuthLayout } from "@/components/AuthLayout";
import { saveProfile } from "@/data/practiceData";
import { apiFetch } from "@/api";

/* Onboarding collects ONLY Board + Class (Abhyas 2.0 academic profile).
   Subjects/chapters/exam details are chosen per-feature, never here. */

const BOARDS = ["CBSE", "ICSE/ISC"];
const CLASSES = ["9", "10", "11", "12"];

const STEP_TITLES = [
  { title: "Pick your board", subtitle: "Your questions will match your board's syllabus." },
  { title: "Pick your class", subtitle: "Practice at exactly your level." },
];

const OptionCard = ({ selected, onClick, testid, children, className = "" }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center justify-center gap-2 rounded-2xl border-2 px-5 py-4 text-sm font-bold transition-[border-color,background-color,transform] duration-200 ${
      selected
        ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
        : "border-[#17201A]/10 bg-[#F8FAF7] text-[#17201A]/70 hover:border-[#16A34A]/40"
    } ${className}`}
  >
    {children}
  </button>
);

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [board, setBoard] = useState(null);
  const [klass, setKlass] = useState(null);

  const canContinue = (step === 0 && board) || (step === 1 && klass);
  const isLast = step === 1;

  const handleFinish = async () => {
    try {
      const data = await apiFetch("/onboarding", {
        method: "POST",
        body: { board, klass },
      });
      saveProfile({ board: data.user.board, klass: data.user.klass });
    } catch {
      saveProfile({ board, klass });
    }
    navigate("/practice-setup");
  };

  return (
    <AuthLayout
      title={STEP_TITLES[step].title}
      subtitle={STEP_TITLES[step].subtitle}
    >
      {/* Progress */}
      <div data-testid="onboarding-progress" className="mb-8 flex items-center gap-2">
        {STEP_TITLES.map((_, i) => (
          <div
            key={i}
            className={`h-2 flex-1 rounded-full transition-colors duration-300 ${
              i <= step ? "bg-[#16A34A]" : "bg-[#17201A]/10"
            }`}
          />
        ))}
        <span className="ml-2 text-xs font-bold text-[#17201A]/50">
          {step + 1}/2
        </span>
      </div>

      {step === 0 && (
        <div data-testid="board-step" className="grid gap-3">
          {BOARDS.map((b) => (
            <OptionCard
              key={b}
              testid={`board-${b === "CBSE" ? "cbse" : "icse"}-btn`}
              selected={board === b}
              onClick={() => setBoard(b)}
              className="py-6 text-base"
            >
              <BookMarked className="h-5 w-5 text-[#16A34A]" />
              {b}
              {board === b && <Check className="h-4 w-4 text-[#16A34A]" />}
            </OptionCard>
          ))}
        </div>
      )}

      {step === 1 && (
        <div data-testid="class-step" className="grid grid-cols-2 gap-3">
          {CLASSES.map((c) => (
            <OptionCard
              key={c}
              testid={`class-${c}-btn`}
              selected={klass === c}
              onClick={() => setKlass(c)}
              className="py-6"
            >
              <span className="font-display text-lg font-extrabold">
                Class {c}
              </span>
            </OptionCard>
          ))}
        </div>
      )}

      <div className="mt-8 flex items-center gap-3">
        {step > 0 && (
          <button
            type="button"
            data-testid="onboarding-back-btn"
            onClick={() => setStep((s) => s - 1)}
            className="inline-flex items-center gap-2 rounded-full border border-[#E3ECE6] px-6 py-3.5 text-sm font-bold text-[#17201A] transition-[border-color,transform] duration-200 hover:border-[#FACC15]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}
        <button
          type="button"
          data-testid={isLast ? "finish-setup-btn" : "onboarding-next-btn"}
          disabled={!canContinue}
          onClick={() => (isLast ? handleFinish() : setStep((s) => s + 1))}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-[#16A34A] py-3.5 text-sm font-bold text-white shadow-sm transition-[background-color,transform,opacity] duration-200 hover:bg-[#15803D] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0"
        >
          {isLast ? "Finish Setup" : "Continue"}
          {!isLast && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
    </AuthLayout>
  );
};

export default Onboarding;
